import { z } from 'zod';
import { DiscountType } from '@/features/dashboard/discounts/types/DiscountType.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';

const optionalPositiveNumber = z.preprocess(
  (val) =>
    val === '' || val === undefined || (typeof val === 'number' && isNaN(val))
      ? null
      : val,
  z.number().positive('Wartość musi być większa niż 0').nullable(),
);

export const discountSchema = z
  .object({
    name: z.string().min(1, 'Nazwa jest wymagana'),
    type: z.enum(DiscountType),
    value: z
      .number({ error: 'Wartość jest wymagana' })
      .positive('Wartość musi być większa niż 0'),
    currency: z.enum(Currency, { error: 'Waluta jest wymagana' }),
    promoCode: z.string().default(''),
    validFrom: z.date().nullable().default(null),
    validTo: z.date().nullable().default(null),
    minParticipants: optionalPositiveNumber,
    minTotalPrice: optionalPositiveNumber,
    maxUsages: optionalPositiveNumber,
    maxDiscountAmount: optionalPositiveNumber,
    combinable: z.boolean().default(false),
    tourIds: z.array(z.string()).default([]),
    active: z.boolean().default(true),
  })
  .refine(
    (data) => data.type !== DiscountType.PERCENTAGE || data.value <= 100,
    {
      message: 'Zniżka procentowa nie może przekraczać 100%',
      path: ['value'],
    },
  )
  .refine(
    (data) =>
      !data.validFrom || !data.validTo || data.validTo >= data.validFrom,
    {
      message: 'Data końca musi być po dacie rozpoczęcia',
      path: ['validTo'],
    },
  )
  .refine((data) => data.promoCode === '' || data.promoCode.length >= 3, {
    message: 'Kod musi mieć minimum 3 znaki',
    path: ['promoCode'],
  })
  .refine((data) => data.promoCode.length <= 100, {
    message: 'Kod może mieć maksymalnie 100 znaków',
    path: ['promoCode'],
  });

export type DiscountFormData = z.infer<typeof discountSchema>;
export type DiscountInputData = z.input<typeof discountSchema>;

export const discountDefaultValues: DiscountInputData = {
  name: '',
  type: DiscountType.PERCENTAGE,
  value: undefined as unknown as number,
  currency: Currency.PLN,
  promoCode: '',
  validFrom: null,
  validTo: null,
  minParticipants: null,
  minTotalPrice: null,
  maxUsages: null,
  maxDiscountAmount: null,
  combinable: false,
  tourIds: [],
  active: true,
};
